import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BadgeCheck, FileUp, Loader2, Eye, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  pending: "bg-[#FFF1C7] text-[#7A5A00]",
  verified: "bg-[#DDF7E7] text-[#14663A]",
  rejected: "bg-[#FFE1E1] text-[#9B2C2C]",
};
export const CredStatus = ({ status }) => (
  <span className={`text-xs rounded-full px-2 py-0.5 capitalize ${STATUS[status] || ""}`} data-testid="credential-status">{status === "pending" ? "Pending review" : status}</span>
);

export async function openCredentialFile(id) {
  try {
    const { data } = await api.get(`/credentials/${id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    toast.error(errMsg(e));
  }
}

const TYPES = ["Texas Standard Certificate", "Texas Probationary Certificate", "Out-of-state teaching certificate", "College degree / diploma", "Subject-area certification", "Other credential"];
const EMPTY = { credential_type: TYPES[0], issuing_state: "TX", certificate_number: "", subjects: "", grade_levels: "", issued_on: "", expires_on: "" };

export function TeacherCredentials() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = useCallback(() => api.get("/credentials/me").then(({ data }) => setRows(data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a PDF, JPG or PNG file");
    if (f.size > 10 * 1024 * 1024) return toast.error("File is larger than 10 MB");
    const fd = new FormData();
    fd.append("file", f);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    setBusy(true);
    try {
      await api.post("/credentials", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Credential uploaded for review");
      setForm(EMPTY);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id) => {
    try { await api.delete(`/credentials/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 mt-6" data-testid="teacher-credentials">
      <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#0B6B6B]" /><h2 className="text-xl font-display font-semibold">Teaching credentials</h2></div>
      <p className="text-sm text-muted-foreground mt-1">Upload certifications and degrees. An administrator verifies each one. Files are private to you, your school and administrators.</p>
      <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Credential type</Label>
          <Select value={form.credential_type} onValueChange={(v) => setForm({ ...form, credential_type: v })}>
            <SelectTrigger data-testid="credential-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Issuing state / institution</Label><Input value={form.issuing_state} onChange={(e) => setForm({ ...form, issuing_state: e.target.value })} data-testid="credential-state-input" /></div>
        <div className="space-y-1.5"><Label>Certificate or ID number</Label><Input value={form.certificate_number} onChange={(e) => setForm({ ...form, certificate_number: e.target.value })} data-testid="credential-number-input" /></div>
        <div className="space-y-1.5"><Label>Subjects</Label><Input value={form.subjects} onChange={(e) => setForm({ ...form, subjects: e.target.value })} placeholder="e.g. Mathematics 7-12" /></div>
        <div className="space-y-1.5"><Label>Grade levels</Label><Input value={form.grade_levels} onChange={(e) => setForm({ ...form, grade_levels: e.target.value })} placeholder="e.g. EC-6" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Issued</Label><Input type="date" value={form.issued_on} onChange={(e) => setForm({ ...form, issued_on: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Expires</Label><Input type="date" value={form.expires_on} onChange={(e) => setForm({ ...form, expires_on: e.target.value })} /></div>
        </div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Document (PDF, JPG or PNG, up to 10 MB)</Label><Input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png" className="h-11 pt-2" data-testid="credential-file-input" /></div>
        <div className="sm:col-span-2"><Button type="submit" disabled={busy} className="h-11" data-testid="credential-upload-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileUp className="h-4 w-4 mr-2" />}Upload for verification</Button></div>
      </form>
      <div className="mt-6">
        {rows === null ? null : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="credentials-empty">No credentials on file yet.</div>
        ) : (
          <ul className="divide-y divide-border" data-testid="credentials-list">
            {rows.map((c) => (
              <li key={c.credential_id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="credential-row">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium flex items-center gap-1">{c.status === "verified" && <BadgeCheck className="h-4 w-4 text-[#14663A]" />}{c.credential_type}</div>
                  <div className="text-xs text-muted-foreground">{[c.issuing_state, c.certificate_number, c.subjects, c.expires_on && `expires ${c.expires_on}`].filter(Boolean).join(" · ")}</div>
                  {c.review_note && <div className="text-xs mt-1">Reviewer note: {c.review_note}</div>}
                </div>
                <CredStatus status={c.status} />
                <Button variant="ghost" size="sm" onClick={() => openCredentialFile(c.credential_id)} data-testid="credential-view-button"><Eye className="h-4 w-4 mr-1" />View</Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(c.credential_id)} aria-label="Remove credential" data-testid="credential-delete-button"><Trash2 className="h-4 w-4" /></Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function CredentialReviewPanel() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const [review, setReview] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => {
    setRows(null);
    api.get("/credentials", { params: status === "all" ? {} : { status } }).then(({ data }) => setRows(data)).catch((e) => { toast.error(errMsg(e)); setRows([]); });
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const decide = async (s) => {
    try {
      await api.post(`/admin/credentials/${review.credential_id}/review`, { status: s, note });
      toast.success(s === "verified" ? "Credential verified" : "Credential rejected");
      setReview(null);
      setNote("");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-white border border-border rounded-[14px] p-4" data-testid="admin-credentials-panel">
      <div className="flex items-center gap-3 mb-3">
        <div className="font-display font-semibold">Teacher credentials</div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 h-9 ml-auto" data-testid="admin-credentials-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="pending">Pending review</SelectItem><SelectItem value="verified">Verified</SelectItem><SelectItem value="rejected">Rejected</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
        </Select>
      </div>
      {rows === null ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="admin-credentials-empty">Nothing here.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Teacher</TableHead><TableHead>Credential</TableHead><TableHead>Uploaded</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.credential_id} data-testid="admin-credential-row">
                  <TableCell><div className="font-medium">{c.teacher_name}</div><div className="text-xs text-muted-foreground">{c.teacher_email}</div></TableCell>
                  <TableCell><div>{c.credential_type}</div><div className="text-xs text-muted-foreground">{[c.issuing_state, c.certificate_number, c.subjects].filter(Boolean).join(" · ")}</div></TableCell>
                  <TableCell className="text-xs">{fmtDate(c.created_at)}</TableCell>
                  <TableCell><CredStatus status={c.status} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => openCredentialFile(c.credential_id)} data-testid="admin-credential-view"><Eye className="h-4 w-4 mr-1" />View</Button>
                    <Button variant="outline" size="sm" onClick={() => { setReview(c); setNote(c.review_note || ""); }} data-testid="admin-credential-review">Review</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Review credential</DialogTitle></DialogHeader>
          {review && (
            <div className="space-y-3 text-sm">
              <div><span className="text-muted-foreground">Teacher:</span> {review.teacher_name} ({review.teacher_email})</div>
              <div><span className="text-muted-foreground">Credential:</span> {review.credential_type} {review.certificate_number && `#${review.certificate_number}`}</div>
              <div className="text-xs text-muted-foreground">For Texas certificates, confirm the number on the TEA Educator Certification lookup before verifying.</div>
              <Button variant="outline" size="sm" onClick={() => openCredentialFile(review.credential_id)}><Eye className="h-4 w-4 mr-1" />Open document</Button>
              <div><Label>Note to teacher (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-credential-note" /></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => decide("rejected")} data-testid="admin-credential-reject">Reject</Button>
            <Button onClick={() => decide("verified")} data-testid="admin-credential-verify">Verify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
